import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Switch from "react-switch";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../themeSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";
import { getActiveGame, ACTIVE_GAMES, getGameCustomizeMeta, getGameDisplayName, resolveGameIconUrl, getGameIconSize, ALL_FILTER } from "../../../utils/gameHelpers";

const MAX_CATEGORIES = 10;
const MAX_ADDL_FIELDS = 8;
const CATEGORY_NAME_MAX_LEN = 30;
const TEXT_MAX_LEN = 100;
const BACK_IMAGE_DIM = 1012;
const ALLOWED_ICON_EXT = ["jpg", "jpeg", "png"];

const isAllCategoryName = (name) => String(name || "").trim().toUpperCase() === ALL_FILTER;

const normalizeKey = (name) =>
  String(name || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const firstEntry = (obj) => Object.entries(obj || {})[0] || [];

const objArrToMap = (arr) =>
  (Array.isArray(arr) ? arr : []).reduce((acc, o) => {
    const [k, v] = firstEntry(o);
    if (k != null) acc[k] = v;
    return acc;
  }, {});

// Categories, icons, placeholder text and message text are stored as parallel,
// index-aligned arrays in the theme. We hydrate them into a single list of
// category objects, preserving each category's original storage key.
function buildCategoriesFromData(data, activeGame) {
  if (activeGame === ACTIVE_GAMES.FREEDOM) {
    const badges = Array.isArray(data?.freedom_badges) ? data.freedom_badges : [];
    return badges
      .map((b) => ({
        name: String(b.name || ""),
        key: b.key || normalizeKey(b.name),
        icon: String(b.image || ""),
        description: "",
        message: "",
      }))
      .filter((c) => c.name && !isAllCategoryName(c.name));
  }
  if (activeGame === ACTIVE_GAMES.BOSS_COUNTS) {
    const cards = Array.isArray(data?.archetype_cards) ? data.archetype_cards : [];
    return cards
      .map((c) => ({
        name: String(c.name || ""),
        key: c.key || normalizeKey(c.name),
        icon: String(c.image || ""),
        description: String(c.description || ""),
        message: "",
      }))
      .filter((c) => c.name && !isAllCategoryName(c.name));
  }

  const names = Array.isArray(data?.default_category_name) ? data.default_category_name : [];
  const icons = Array.isArray(data?.category_icons) ? data.category_icons : [];
  const descMap = objArrToMap(data?.category_placeholder_text);
  const msgMap = objArrToMap(data?.category_message_text);

  return names
    .map((name, i) => {
      const [iconKey, iconVal] = firstEntry(icons[i]);
      const key = iconKey || normalizeKey(name);
      return {
        name: String(name || ""),
        key,
        icon: String(iconVal || ""),
        description: String(descMap[key] ?? ""),
        message: String(msgMap[key] ?? ""),
      };
    })
    .filter((c) => c.name && !isAllCategoryName(c.name));
}

export default function BadgeCategoryEditor({ data, currentTheme, adminToken }) {
  const dispatch = useDispatch();

  const [categories, setCategories] = useState([]);
  const [descEnabled, setDescEnabled] = useState(false);
  const [msgEnabled, setMsgEnabled] = useState(false);
  const [addlEnabled, setAddlEnabled] = useState(false);
  const [defaultMsg, setDefaultMsg] = useState("");
  const [addlFields, setAddlFields] = useState([]);

  const [newTitle, setNewTitle] = useState("");
  const [iconCatIndex, setIconCatIndex] = useState("");
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState("");
  const [iconUploading, setIconUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const iconFileRef = useRef(null);

  const [backImageFile, setBackImageFile] = useState(null);
  const [backImagePreview, setBackImagePreview] = useState("");
  const [backImageUploading, setBackImageUploading] = useState(false);
  const [backImageName, setBackImageName] = useState("");
  const backImageFileRef = useRef(null);

  const S3 = process.env.REACT_APP_S3_PATH || "";
  const resolveAssetUrl = (path) => {
    const raw = String(path || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const rel = raw.replace(/^\/+/, "");
    return `${S3}${encodeURIComponent(rel).replace(/%2F/g, "/")}`;
  };

  useEffect(() => {
    if (!data) return;
    const game = getActiveGame(data);
    if (
      "default_category_name" in data ||
      "category_icons" in data ||
      "freedom_badges" in data ||
      "archetype_cards" in data
    ) {
      setCategories(buildCategoriesFromData(data, game));
    }
    if ("category_description" in data) {
      setDescEnabled(String(data.category_description) === "true");
    }
    if ("category_message_status" in data) {
      setMsgEnabled(String(data.category_message_status) === "true");
    }
    if ("additional_fields" in data) {
      setAddlEnabled(String(data.additional_fields) === "true");
    }
    if ("deafult_message_text" in data) {
      setDefaultMsg(String(data.deafult_message_text ?? ""));
    }
    if ("additional_new_fields" in data) {
      setAddlFields(Array.isArray(data.additional_new_fields) ? data.additional_new_fields : []);
    }
    if ("category_back_image" in data) {
      setBackImageName(String(data.category_back_image || "back.png"));
    } else if (game === ACTIVE_GAMES.BOSS_COUNTS) {
      setBackImageName("back.png");
    }
  }, [data]);

  const activeGame = getActiveGame(data);
  const gameMeta = getGameCustomizeMeta(activeGame);
  const gameDisplayName = getGameDisplayName(activeGame);
  const isCheers = activeGame === ACTIVE_GAMES.CHEERS;
  const isBossCounts = activeGame === ACTIVE_GAMES.BOSS_COUNTS;
  const isFreedom = activeGame === ACTIVE_GAMES.FREEDOM;
  const isMensDay = activeGame === ACTIVE_GAMES.MENS_DAY;
  const isBadges = activeGame === ACTIVE_GAMES.BADGES;
  // Freedom / Men's Day / Badges of Trust: no category-description toggle
  const showDescription = !isFreedom && !isMensDay && !isBadges;
  const iconSize = getGameIconSize(activeGame);
  const iconW = iconSize.width;
  const iconH = iconSize.height;

  const resolvePreviewUrl = (path) => {
    const raw = String(path || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const viaGames = resolveGameIconUrl(raw);
    if (viaGames) return viaGames;
    return resolveAssetUrl(raw);
  };

  const buildArrays = (cats) => {
    const base = {
      default_category_name: cats.map((c) => c.name),
      category_icons: cats.map((c) => ({ [c.key]: c.icon || "" })),
      category_placeholder_text: cats.map((c) => ({ [c.key]: c.description || "" })),
      category_message_text: cats.map((c) => ({ [c.key]: c.message || "" })),
      category_icon_width: iconW,
      category_icon_height: iconH,
    };
    if (activeGame === ACTIVE_GAMES.BOSS_COUNTS) {
      base.category_icon_dimension = iconW;
      base.archetype_cards = cats.map((c) => ({
        key: c.key,
        name: c.name,
        image: c.icon,
        description: c.description,
      }));
    }
    if (activeGame === ACTIVE_GAMES.FREEDOM) {
      base.freedom_badges = cats.map((c) => ({
        key: c.key,
        name: c.name,
        image: c.icon,
      }));
    }
    return base;
  };

  const persist = async (extra, successMsg, cats = categories) => {
    if (!currentTheme) {
      Swal.fire("No theme", "Select a theme first.", "error");
      return false;
    }
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          currentTheme,
          data: { ...buildArrays(cats), ...extra },
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", successMsg || "Saved.", "success");
      return true;
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed.", "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateCategoryField = (index, field, value) => {
    setCategories((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const handleAddTitle = () => {
    const name = newTitle.trim();
    if (!name) return;
    if (isAllCategoryName(name)) {
      Swal.fire("Not allowed", 'The category name "ALL" is reserved and cannot be added.', "warning");
      return;
    }
    if (name.length > CATEGORY_NAME_MAX_LEN) {
      Swal.fire("Too long", `Title can be at most ${CATEGORY_NAME_MAX_LEN} characters.`, "warning");
      return;
    }
    if (categories.length >= MAX_CATEGORIES) {
      Swal.fire("Limit reached", `You can add at most ${MAX_CATEGORIES} titles.`, "warning");
      return;
    }
    const key = normalizeKey(name);
    if (categories.some((c) => c.key === key)) {
      Swal.fire("Duplicate", "A category with this name already exists.", "warning");
      return;
    }
    setCategories((prev) => [...prev, { name, key, icon: "", description: "", message: "" }]);
    setNewTitle("");
  };

  const handleRemoveCategory = (index) => {
    const cat = categories[index];
    Swal.fire({
      title: "Remove title?",
      text: `"${cat?.name}" and its icon/description/message will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove",
    }).then((r) => {
      if (!r.isConfirmed) return;
      setCategories((prev) => prev.filter((_, i) => i !== index));
      setIconCatIndex("");
    });
  };

  const handleSaveTitlesDescriptions = () =>
    persist(
      {
        category_description: isBossCounts ? "true" : showDescription && descEnabled ? "true" : "false",
        category_icon_width: iconW,
        category_icon_height: iconH,
        ...(isBossCounts ? { category_icon_dimension: iconW } : {}),
      },
      "Categories saved."
    );

  const handleSaveMessages = () =>
    persist(
      {
        category_message_status: msgEnabled ? "true" : "false",
        deafult_message_text: defaultMsg,
      },
      "Messages saved."
    );

  const handleSaveAdditionalFields = () =>
    persist(
      {
        additional_fields: addlEnabled ? "true" : "false",
        additional_new_fields: addlFields,
      },
      "Additional fields saved."
    );

  const toggleFieldSelected = (index) => {
    setAddlFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, selected: !f.selected } : f))
    );
  };

  const handleRemoveField = (index) => {
    setAddlFields((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddField = async () => {
    if (addlFields.length >= MAX_ADDL_FIELDS) {
      Swal.fire("Limit reached", `You can add at most ${MAX_ADDL_FIELDS} fields.`, "warning");
      return;
    }
    const { value: name } = await Swal.fire({
      title: "Add input field",
      input: "text",
      inputLabel: "Field name",
      inputPlaceholder: "e.g. department",
      showCancelButton: true,
      inputValidator: (v) => (!String(v || "").trim() ? "Field name is required" : undefined),
    });
    if (!name) return;
    const label = String(name).trim();
    const cleaned = label.toLowerCase().replace(/\s+/g, "_");
    if (addlFields.some((f) => f.name === cleaned)) {
      Swal.fire("Duplicate", "A field with this name already exists.", "warning");
      return;
    }
    setAddlFields((prev) => [
      ...prev,
      { name: cleaned, label, type: "text", selected: false },
    ]);
  };

  const handleIconFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_ICON_EXT.includes(ext)) {
      Swal.fire("Invalid file", "Only PNG, JPG, and JPEG are allowed.", "error");
      if (iconFileRef.current) iconFileRef.current.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("File too large", "Maximum 5 MB.", "error");
      if (iconFileRef.current) iconFileRef.current.value = "";
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width !== iconW || img.height !== iconH) {
        URL.revokeObjectURL(url);
        Swal.fire(
          "Wrong dimensions",
          `Icon must be exactly ${iconW} × ${iconH} px. Uploaded image is ${img.width} × ${img.height} px.`,
          "error"
        );
        if (iconFileRef.current) iconFileRef.current.value = "";
        return;
      }
      if (iconPreview) URL.revokeObjectURL(iconPreview);
      setIconFile(file);
      setIconPreview(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      Swal.fire("Invalid image", "Could not read image dimensions.", "error");
      if (iconFileRef.current) iconFileRef.current.value = "";
    };
    img.src = url;
  };

  const handleUploadIcon = async () => {
    if (iconCatIndex === "" || !categories[iconCatIndex]) {
      Swal.fire("Select a category", "Please choose a category first.", "warning");
      return;
    }
    if (!iconFile) {
      Swal.fire("No file", "Please choose an icon image to upload.", "warning");
      return;
    }
    setIconUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", iconFile);
      fd.append("themeName", String(currentTheme));
      const res = await fetch(apiUrl("/uploadCategoryImage"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");

      const index = Number(iconCatIndex);
      const nextCategories = categories.map((c, i) =>
        i === index ? { ...c, icon: json.filename || "" } : c
      );
      setCategories(nextCategories);
      if (iconPreview) URL.revokeObjectURL(iconPreview);
      setIconFile(null);
      setIconPreview("");
      if (iconFileRef.current) iconFileRef.current.value = "";
      await persist({}, "Category icon updated.", nextCategories);
    } catch (e) {
      Swal.fire("Error", e.message || "Upload failed.", "error");
    } finally {
      setIconUploading(false);
    }
  };

  const validateSquareImage = (file, dim) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        if (img.width !== dim || img.height !== dim) {
          URL.revokeObjectURL(url);
          reject(
            new Error(
              `Image must be exactly ${dim} × ${dim} px. Uploaded image is ${img.width} × ${img.height} px.`
            )
          );
          return;
        }
        resolve(url);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Could not read image dimensions."));
      };
      img.src = url;
    });

  const handleBackImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_ICON_EXT.includes(ext)) {
      Swal.fire("Invalid file", "Only PNG, JPG, and JPEG are allowed.", "error");
      if (backImageFileRef.current) backImageFileRef.current.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("File too large", "Maximum 5 MB.", "error");
      if (backImageFileRef.current) backImageFileRef.current.value = "";
      return;
    }
    try {
      const url = await validateSquareImage(file, BACK_IMAGE_DIM);
      if (backImagePreview) URL.revokeObjectURL(backImagePreview);
      setBackImageFile(file);
      setBackImagePreview(url);
    } catch (err) {
      Swal.fire("Wrong dimensions", err.message || "Invalid image.", "error");
      if (backImageFileRef.current) backImageFileRef.current.value = "";
    }
  };

  const handleUploadBackImage = async () => {
    if (!backImageFile) {
      Swal.fire("No file", "Please choose a flip-card back image to upload.", "warning");
      return;
    }
    setBackImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", backImageFile);
      fd.append("themeName", String(currentTheme));
      const res = await fetch(apiUrl("/uploadCategoryImage"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");

      const filename = json.filename || "";
      setBackImageName(filename);
      if (backImagePreview) URL.revokeObjectURL(backImagePreview);
      setBackImageFile(null);
      setBackImagePreview("");
      if (backImageFileRef.current) backImageFileRef.current.value = "";
      await persist(
        { category_back_image: filename, category_icon_dimension: iconW, category_icon_width: iconW, category_icon_height: iconH },
        "Flip-card back image saved."
      );
    } catch (e) {
      Swal.fire("Error", e.message || "Upload failed.", "error");
    } finally {
      setBackImageUploading(false);
    }
  };

  return (
    <div className="badgemgr">
      <div className="catmgr-header">
        <div className="catmgr-header-left">
          <h2 className="catmgr-title">
            <i className="fa-solid fa-medal" /> &nbsp;{gameMeta.pageTitle}
          </h2>
          <p className="catmgr-subtitle">
            {gameDisplayName} · {categories.length} / {MAX_CATEGORIES} {gameMeta.categoryLabel.toLowerCase()}s
            · icons {iconW} × {iconH} px
          </p>
        </div>
      </div>

      <div className="badgemgr-grid">
        {/* ── Left column ─────────────────────────────────────────── */}
        <div className="badgemgr-col">
          <div className="badgemgr-section">
            <h3 className="badgemgr-section-title">{gameMeta.categoryLabel}s</h3>
            <div className="badgemgr-chips">
              {categories.length === 0 ? (
                <p className="catmgr-no-items">No titles yet. Add one below.</p>
              ) : (
                categories.map((c, i) => (
                  <span className="badgemgr-chip" key={c.key}>
                    {c.name}
                    <button
                      type="button"
                      className="badgemgr-chip-x"
                      title="Remove"
                      onClick={() => handleRemoveCategory(i)}
                      disabled={saving}
                    >
                      &times;
                    </button>
                  </span>
                ))
              )}
            </div>

            <h3 className="badgemgr-section-title" style={{ marginTop: 18 }}>
              {gameMeta.addTitleLabel}:
            </h3>
            <input
              className="catmgr-modal-input"
              value={newTitle}
              maxLength={CATEGORY_NAME_MAX_LEN}
              placeholder={`New ${gameMeta.categoryLabel.toLowerCase()}`}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddTitle();
              }}
            />
            <p className="catmgr-modal-hint">
              * You can add {MAX_CATEGORIES} titles atmost.
              <br />* Character limit for the title name is {CATEGORY_NAME_MAX_LEN}.
            </p>
            <button
              type="button"
              className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
              onClick={handleAddTitle}
              disabled={saving}
            >
              <i className="fa-solid fa-plus" /> Add Title
            </button>
            {isFreedom && (
              <button
                type="button"
                className="catmgr-btn catmgr-btn-primary catmgr-btn-sm"
                style={{ marginLeft: 8 }}
                onClick={handleSaveTitlesDescriptions}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            )}
          </div>

          {showDescription && (
          <div className="badgemgr-section">
            {!isBossCounts ? (
              <div className="badgemgr-toggle-row">
                <Switch
                  checked={descEnabled}
                  onChange={setDescEnabled}
                  onColor="#E25569"
                  offColor="#cbd5e1"
                  height={22}
                  width={44}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <span className="badgemgr-toggle-label">Enable {gameMeta.descriptionLabel.toLowerCase()}</span>
              </div>
            ) : null}

            {(isBossCounts || descEnabled) && (
              <div className="badgemgr-fields">
                <h3 className="badgemgr-section-title">{gameMeta.descriptionLabel}s:</h3>
                {categories.map((c, i) => (
                  <div className="badgemgr-field" key={c.key}>
                    <label className="badgemgr-field-label">{c.name}:</label>
                    <input
                      className="catmgr-modal-input"
                      value={c.description}
                      maxLength={TEXT_MAX_LEN}
                      onChange={(e) =>
                        updateCategoryField(i, "description", e.target.value.slice(0, TEXT_MAX_LEN))
                      }
                    />
                  </div>
                ))}
                <p className="catmgr-modal-hint">
                  * Add description text up to {TEXT_MAX_LEN} characters.
                </p>
              </div>
            )}

            {isBossCounts && (
              <div className="badgemgr-fields" style={{ marginTop: 16 }}>
                <h3 className="badgemgr-section-title">Flip-card back image</h3>
                <p className="catmgr-modal-hint">
                  Description text is shown on this image when the card flips.
                </p>
                <div className="catmgr-upload-preview" style={{ marginBottom: 10 }}>
                  {backImagePreview ? (
                    <img src={backImagePreview} alt="back preview" className="catmgr-upload-thumb" />
                  ) : backImageName || data?.category_back_image ? (
                    <img
                      src={resolvePreviewUrl(backImageName || data?.category_back_image || "back.png")}
                      alt="current back"
                      className="catmgr-upload-thumb"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="catmgr-upload-placeholder">
                      <i className="fa-solid fa-image" />
                      <span>
                        {BACK_IMAGE_DIM} × {BACK_IMAGE_DIM}
                      </span>
                    </div>
                  )}
                </div>
                <input
                  ref={backImageFileRef}
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  className="catmgr-file-input"
                  onChange={handleBackImageFileChange}
                />
                <p className="catmgr-modal-hint">
                  * Allowed Photo Formats - PNG, JPG, JPEG
                  <br />* Flip-card back image size must be {BACK_IMAGE_DIM}×{BACK_IMAGE_DIM}
                </p>
                <button
                  type="button"
                  className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                  onClick={handleUploadBackImage}
                  disabled={backImageUploading || saving}
                >
                  {backImageUploading ? "Uploading…" : "Upload back image"}
                </button>
              </div>
            )}

            <button
              type="button"
              className="catmgr-btn catmgr-btn-primary catmgr-btn-sm"
              style={{ marginTop: 10 }}
              onClick={handleSaveTitlesDescriptions}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
          )}

          {isCheers && (
          <div className="badgemgr-section">
            <div className="badgemgr-toggle-row">
              <Switch
                checked={addlEnabled}
                onChange={setAddlEnabled}
                onColor="#E25569"
                offColor="#cbd5e1"
                height={22}
                width={44}
                uncheckedIcon={false}
                checkedIcon={false}
              />
              <span className="badgemgr-toggle-label">Add additional text fields</span>
            </div>

            {addlEnabled && (
              <div className="badgemgr-fields">
                <p className="catmgr-pair-hint">
                  * Select the required field and click 'Save' to confirm.
                </p>
                {addlFields.map((f, i) => (
                  <div className="badgemgr-addl-row" key={f.name}>
                    <span className="badgemgr-addl-name">
                      {f.label || f.name}
                      {f.label && f.label !== f.name ? (
                        <span className="badgemgr-addl-key"> ({f.name})</span>
                      ) : null}
                    </span>
                    <div className="badgemgr-addl-controls">
                      <button
                        type="button"
                        className={`badgemgr-dot ${f.selected ? "badgemgr-dot--on" : ""}`}
                        title={f.selected ? "Selected" : "Not selected"}
                        onClick={() => toggleFieldSelected(i)}
                      />
                      <button
                        type="button"
                        className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                        onClick={() => handleRemoveField(i)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                <p className="catmgr-modal-hint">
                  {addlFields.length} / {MAX_ADDL_FIELDS} fields
                </p>
                <div className="badgemgr-addl-actions">
                  <button
                    type="button"
                    className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                    onClick={handleAddField}
                    disabled={addlFields.length >= MAX_ADDL_FIELDS}
                  >
                    Add Input
                  </button>
                  <button
                    type="button"
                    className="catmgr-btn catmgr-btn-primary catmgr-btn-sm"
                    onClick={handleSaveAdditionalFields}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}
          </div>
          )}
        </div>

        {/* ── Right column ────────────────────────────────────────── */}
        <div className="badgemgr-col">
          <div className="badgemgr-section">
            <h3 className="badgemgr-section-title">Add {gameMeta.iconLabel}:</h3>
            <select
              className="catmgr-modal-input"
              value={iconCatIndex}
              onChange={(e) => setIconCatIndex(e.target.value)}
            >
              <option value="">Select {gameMeta.categoryLabel}</option>
              {categories.map((c, i) => (
                <option value={i} key={c.key}>
                  {c.name}
                </option>
              ))}
            </select>

            {iconCatIndex !== "" && categories[iconCatIndex] && (
              <div className="badgemgr-icon-current">
                <div className="catmgr-upload-preview catmgr-upload-preview--square">
                  {iconPreview ? (
                    <img src={iconPreview} alt="preview" className="catmgr-upload-thumb" />
                  ) : categories[iconCatIndex].icon ? (
                    <img
                      src={resolveGameIconUrl(categories[iconCatIndex].icon) || resolveAssetUrl(categories[iconCatIndex].icon)}
                      alt="current icon"
                      className="catmgr-upload-thumb"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="catmgr-upload-placeholder">
                      <i className="fa-solid fa-image" />
                      <span>
                        {iconW} × {iconH}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <h3 className="badgemgr-section-title" style={{ marginTop: 12 }}>
              Add Category Icon
            </h3>
            <input
              ref={iconFileRef}
              type="file"
              accept=".png,.jpg,.jpeg"
              className="catmgr-file-input"
              onChange={handleIconFileChange}
            />
            <p className="catmgr-modal-hint">
              * Allowed Photo Formats - PNG, JPG, JPEG
              <br />* Icon image size must be {iconW}×{iconH}
            </p>
            <button
              type="button"
              className="catmgr-btn catmgr-btn-primary catmgr-btn-sm"
              onClick={handleUploadIcon}
              disabled={iconUploading || saving}
            >
              {iconUploading ? "Uploading…" : "Upload"}
            </button>
          </div>

          {isCheers && (
          <div className="badgemgr-section">
            <div className="badgemgr-toggle-row">
              <Switch
                checked={msgEnabled}
                onChange={setMsgEnabled}
                onColor="#E25569"
                offColor="#cbd5e1"
                height={22}
                width={44}
                uncheckedIcon={false}
                checkedIcon={false}
              />
              <span className="badgemgr-toggle-label">Enable Message for category</span>
            </div>

            {msgEnabled ? (
              <div className="badgemgr-fields">
                <h3 className="badgemgr-section-title">Add message for badges:</h3>
                {categories.map((c, i) => (
                  <div className="badgemgr-field" key={c.key}>
                    <label className="badgemgr-field-label">{c.name}:</label>
                    <input
                      className="catmgr-modal-input"
                      value={c.message}
                      maxLength={TEXT_MAX_LEN}
                      onChange={(e) =>
                        updateCategoryField(i, "message", e.target.value.slice(0, TEXT_MAX_LEN))
                      }
                    />
                  </div>
                ))}
                <p className="catmgr-modal-hint">
                  * Add message text up to {TEXT_MAX_LEN} characters.
                </p>
              </div>
            ) : (
              <div className="badgemgr-fields">
                <h3 className="badgemgr-section-title">Default message</h3>
                <textarea
                  className="catmgr-modal-input"
                  rows={3}
                  value={defaultMsg}
                  onChange={(e) => setDefaultMsg(e.target.value)}
                  placeholder="Default message shown for all badges"
                />
                <p className="catmgr-modal-hint">
                  Shown for every badge while per-category messages are disabled.
                </p>
              </div>
            )}

            <button
              type="button"
              className="catmgr-btn catmgr-btn-primary catmgr-btn-sm"
              style={{ marginTop: 10 }}
              onClick={handleSaveMessages}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
